// src/Services/orderTrackingService.ts
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../Firebase/Config';

const ORDERS_COLLECTION = 'orders';

export interface TrackingItem {
  productId: string;
  productName: string;
  productSku: string;
  productImage?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  variantId?: string | null;
  variantSelections?: Array<{ variantType: string; variantValue: string }> | null;
  variantSku?: string | null;
}

export interface ShippingAddress {
  fullName: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phoneNumber: string;
}

export type OrderStatus =
  | 'pending'
  | 'confirmed'
  | 'processing'
  | 'shipped'
  | 'delivered'
  | 'cancelled'
  | 'returned';

export type PaymentStatus = 'pending' | 'paid' | 'failed' | 'refunded';

export interface TrackedOrder {
  id: string;
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  items: TrackingItem[];
  subtotal: number;
  taxAmount: number;
  shippingCost: number;
  discountAmount: number;
  totalAmount: number;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  paymentMethod: string;
  shippingAddress: ShippingAddress;
  billingAddress?: ShippingAddress;
  notes?: string | null;
  trackingNumber?: string | null;
  estimatedDelivery?: string | null;
  orderDate: Date;
  updatedAt: Date;
  stockReservationFailed?: boolean;
}

// Status step definitions for the progress tracker
export const ORDER_STATUS_STEPS: {
  key: OrderStatus;
  label: string;
  icon: string;
  description: string;
}[] = [
    {
      key: 'pending',
      label: 'Order Placed',
      icon: 'zmdi-check',
      description: 'Your order has been received.',
    },
    {
      key: 'confirmed',
      label: 'Confirmed',
      icon: 'zmdi-thumb-up',
      description: 'Your order has been confirmed.',
    },
    {
      key: 'processing',
      label: 'Processing',
      icon: 'zmdi-settings',
      description: 'We are preparing your order.',
    },
    {
      key: 'shipped',
      label: 'Shipped',
      icon: 'zmdi-truck',
      description: 'Your order is on the way.',
    },
    {
      key: 'delivered',
      label: 'Delivered',
      icon: 'zmdi-home',
      description: 'Your order has been delivered.',
    },
  ];

// Returns the step index (0-based) for a given status.
// Cancelled / returned are treated as special cases.
export const getStatusStepIndex = (status: OrderStatus): number => {
  const idx = ORDER_STATUS_STEPS.findIndex((s) => s.key === status);
  return idx === -1 ? 0 : idx;
};

// ── Safe date converter (handles Firestore Timestamp, Date, string, number) ──
const toDate = (value: any): Date => {
  if (!value) return new Date();
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  return new Date(value);
};

// ── Fetch a single order by its Firestore document ID ───────────────────────
export const fetchOrderById = async (
  orderId: string
): Promise<TrackedOrder | null> => {
  try {
    const orderRef = doc(db, ORDERS_COLLECTION, orderId);
    const snap = await getDoc(orderRef);

    if (!snap.exists()) return null;

    return mapDocToOrder(snap.id, snap.data());
  } catch (error) {
    console.error('Error fetching order by ID:', error);
    return null;
  }
};

// ── Fetch orders by order number (displayed on confirmation page) ────────────
export const fetchOrderByNumber = async (
  orderNumber: string
): Promise<TrackedOrder | null> => {
  try {
    const ordersRef = collection(db, ORDERS_COLLECTION);
    const q = query(ordersRef, where('orderNumber', '==', orderNumber.trim()));
    const snap = await getDocs(q);

    if (snap.empty) return null;

    const firstDoc = snap.docs[0];
    return mapDocToOrder(firstDoc.id, firstDoc.data());
  } catch (error) {
    console.error('Error fetching order by number:', error);
    return null;
  }
};

// ── Search by order number AND email ────────────────────────────────────────
// Returns the first matching order.
export const searchOrder = async (
  orderNumber: string,
  email: string
): Promise<{ order: TrackedOrder | null; error: string | null }> => {
  try {
    if (!orderNumber.trim()) {
      return { order: null, error: 'Please enter your order number.' };
    }
    if (!email.trim()) {
      return { order: null, error: 'Please enter your email address.' };
    }

    debugger
    const ordersRef = collection(db, ORDERS_COLLECTION);
    const q = query(
      ordersRef,
      where('orderNumber', '==', orderNumber.trim()),
      where('customerEmail', '==', email.trim())
    );
    const snap = await getDocs(q);

    if (snap.empty) {
      return {
        order: null,
        error:
          'No order found with that order number and email. Please check your details.',
      };
    }

    const firstDoc = snap.docs[0];
    return {
      order: mapDocToOrder(firstDoc.id, firstDoc.data()),
      error: null,
    };
  } catch (error) {
    console.error('Error searching order:', error);
    return {
      order: null,
      error: 'Something went wrong. Please try again.',
    };
  }
};

// ── Fetch all orders placed under a given email (used by the account page) ──
// Sorted client-side by orderDate desc — avoids requiring a composite index
// for a where(customerEmail) + orderBy(orderDate) query.
export const getOrdersByEmail = async (email: string): Promise<TrackedOrder[]> => {
  try {
    const ordersRef = collection(db, ORDERS_COLLECTION);
    const q = query(ordersRef, where('customerEmail', '==', email.trim()));
    const snap = await getDocs(q);
    const orders = snap.docs.map((d) => mapDocToOrder(d.id, d.data()));
    return orders.sort((a, b) => b.orderDate.getTime() - a.orderDate.getTime());
  } catch (error) {
    console.error('Error fetching orders by email:', error);
    return [];
  }
};

// ── Map Firestore document data → TrackedOrder ───────────────────────────────
const mapDocToOrder = (id: string, data: any): TrackedOrder => {
  return {
    id,
    orderNumber: data.orderNumber || '',
    customerName: data.customerName || '',
    customerEmail: data.customerEmail || '',
    customerPhone: data.customerPhone || undefined,
    items: (data.items || []).map((item: any) => ({
      productId: item.productId || '',
      productName: item.productName || '',
      productSku: item.productSku || '',
      productImage: item.productImage || undefined,
      quantity: item.quantity || 0,
      unitPrice: item.unitPrice || 0,
      totalPrice: item.totalPrice || 0,
      variantId: item.variantId || null,
      variantSelections: item.variantSelections || null,
      variantSku: item.variantSku || null,
    })),
    subtotal: data.subtotal || 0,
    taxAmount: data.taxAmount || 0,
    shippingCost: data.shippingCost || 0,
    discountAmount: data.discountAmount || 0,
    totalAmount: data.totalAmount || 0,
    status: (data.status as OrderStatus) || 'pending',
    paymentStatus: (data.paymentStatus as PaymentStatus) || 'pending',
    paymentMethod: data.paymentMethod || '',
    shippingAddress: data.shippingAddress || {},
    billingAddress: data.billingAddress || undefined,
    notes: data.notes || null,
    trackingNumber: data.trackingNumber || null,
    estimatedDelivery: data.estimatedDelivery
      ? typeof data.estimatedDelivery.toDate === 'function'
        ? data.estimatedDelivery.toDate().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
        : String(data.estimatedDelivery)
      : null,
    orderDate: toDate(data.orderDate),
    updatedAt: data.updatedAt?._methodName === 'serverTimestamp'
      ? new Date()
      : toDate(data.updatedAt),
    stockReservationFailed: data.stockReservationFailed || false,
  };
};

// ── Helpers ──────────────────────────────────────────────────────────────────
export const getVariantText = (
  variantSelections?: Array<{ variantType: string; variantValue: string }> | null
): string => {
  if (!variantSelections || variantSelections.length === 0) return '';
  return variantSelections
    .map((s) => `${s.variantType}: ${s.variantValue}`)
    .join(', ');
};

export const getStatusColor = (status: OrderStatus): string => {
  switch (status) {
    case 'pending':
      return '#ffc107';
    case 'confirmed':
      return '#17a2b8';
    case 'processing':
      return '#717fe0';
    case 'shipped':
      return '#fd7e14';
    case 'delivered':
      return '#28a745';
    case 'cancelled':
      return '#dc3545';
    case 'returned':
      return '#6c757d';
    default:
      return '#ffc107';
  }
};

export const getPaymentStatusColor = (status: PaymentStatus): string => {
  switch (status) {
    case 'paid':
      return '#28a745';
    case 'pending':
      return '#ffc107';
    case 'failed':
      return '#dc3545';
    case 'refunded':
      return '#6c757d';
    default:
      return '#ffc107';
  }
};